import { createContext, useCallback, useContext, useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Link, NavLink, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "@/components/ui/sonner";
import DayCareOverview from "@/components/DayCareOverview";
import CaregiverOverview from "@/components/CaregiverOverview";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AdminKnowledgeModule, KnowledgeArticlePage, KnowledgeHomeBlocks, KnowledgeNewsPage } from "@/components/KnowledgeNews";
import handsMark from "@/assets/rumah-kasih-hands-cropped.png";
import heroImage from "@/assets/rumah-kasih-hero.webp";
import {
  ArrowRight, CalendarDays, CheckCircle2, Clock3, Facebook, HandHeart,
  Heart, Instagram, Loader2, Mail, MapPin, Menu, MessageCircle,
  Phone, Plus, Save, ShieldCheck, Trash2, Users, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const ORGANIZATION = {
  name: "Stichting Rumah Kasih",
  streetAddress: "Eelinkstraat 57",
  postalAddress: "7101 JK Winterswijk",
  chamberOfCommerce: "81284381"
};
const DEFAULT_SITE_DATA = {
  content: {
    hero_title: "Dagbesteding met herkenning",
    hero_title_size: "normal",
    hero_text: "Iedere dinsdag in Winterswijk voor Molukse en Indische ouderen, en ouderen met affiniteit met deze gemeenschappen. Met aandacht voor dementie en ruimte voor mantelzorgers.",
    hero_image_url: "",
    hero_image_position: "center",
    home_intro_title: "Goede zorg begint met herkenning",
    home_intro_text: "Dementie verandert veel, maar vertrouwde taal, smaken, muziek en omgang blijven betekenis dragen. Rumah Kasih wil passende aandacht voor ouderen verbinden met betrouwbare ontlasting voor mantelzorgers.",
    approach_label: "Onze aanpak",
    approach_title: "Van urgente behoefte naar duurzame zorg",
    approach_text: "We bouwen tegelijk aan zorgkwaliteit, samenwerking met gemeente en zorgpartners, en financiering vanuit fondsen, gemeenschap en betrokken organisaties.",
    agenda_title: "Bijeenkomsten en updates",
    agenda_intro: "De dagbesteding vindt iedere dinsdag plaats van 10.00 tot 13.00 uur. Hier publiceren we afwijkende data, informatieavonden en andere belangrijke momenten.",
    support_title: "Help deze plek mogelijk maken",
    support_text: "Uw bijdrage ondersteunt de voorbereiding en opstart van cultureel passende dagbesteding. We werken aan een concreet campagnebedrag en publiceren de besteding zodra de opstartbegroting is afgerond.",
    support_item_1: "Voorbereiding van zorg en kwaliteit",
    support_item_2: "Opbouw van samenwerkingen en financiering",
    support_item_3: "Zichtbaarheid en bereik onder families",
    contact_title: "Praat met ons mee",
    contact_text: "Bent u mantelzorger, zorgprofessional, fonds, vrijwilliger of mogelijke samenwerkingspartner? We horen graag wat u nodig heeft of kunt bijdragen.",
    footer_text: "Cultureel passende dagbesteding met Molukse en Indische herkenning, voor ouderen en de mensen die dagelijks voor hen zorgen.",
    daycare_label: "Dagbesteding",
    daycare_title: "Een dag die vertrouwd voelt",
    daycare_intro_1: "Rumah Kasih biedt dagbesteding in Winterswijk voor Molukse en Indische ouderen en voor ouderen met affiniteit met deze gemeenschappen, bijvoorbeeld door hun geboorte, levensgeschiedenis of culturele verbondenheid.",
    daycare_intro_2: "Er is extra aandacht voor deelnemers met dementie, Alzheimer en andere hersenaandoeningen.",
    daycare_intro_3: "Cultuur staat niet los op het programma, maar krijgt een plek in de hele dag: in hoe mensen worden begroet, samen eten, herinneringen delen en contact maken.",
    daycare_image_url: "",
    daycare_image_alt: "Oudere deelnemers die samen aan tafel een rustige activiteit doen",
    daycare_features_label: "Herkenning in de hele dag",
    daycare_features_title: "Wat vertrouwdheid concreet betekent",
    daycare_feature_1_title: "Smaken en gewoonten",
    daycare_feature_1_text: "Vertrouwde gerechten, geuren en dagelijkse rituelen die herinneringen en gesprek op gang brengen.",
    daycare_feature_2_title: "Taal, muziek en verhalen",
    daycare_feature_2_text: "Ruimte voor moedertaal, bekende muziek en persoonlijke geschiedenis uit de Molukse en Indische gemeenschap.",
    daycare_feature_3_title: "Rust, ritme en aandacht",
    daycare_feature_3_text: "Een overzichtelijke dagstructuur die aansluit bij wat deelnemers aankunnen en prettig vinden.",
    daycare_status_label: "Iedere dinsdag van 10.00 tot 13.00 uur",
    daycare_status_title: "De dagbesteding is gestart",
    daycare_status_text: "Dinsdag 14 juli 2026 is de laatste bijeenkomst voor de zomerstop. Daarna is Rumah Kasih zes weken gesloten. Actuele data publiceren we in de agenda.",
    caregiver_label: "Voor mantelzorgers",
    caregiver_title: "Ruimte om op adem te komen",
    caregiver_intro_1: "Veel families dragen langdurig zorg voor een ouder of naaste. Dat vraagt liefde en geduld, maar ook steeds meer praktische en emotionele kracht.",
    caregiver_intro_2: "De dagbesteding biedt tijdelijke ontlasting, met het vertrouwen dat uw naaste op een plek is waar achtergrond, gewoonten en persoonlijke behoeften serieus worden genomen.",
    caregiver_image_url: "",
    caregiver_image_alt: "Ouderen die in een rustige omgeving persoonlijke aandacht krijgen",
    caregiver_needs_label: "Wat dit kan betekenen",
    caregiver_needs_title: "Zorgen hoeft niet zonder ondersteuning",
    caregiver_need_1_title: "Vertrouwen",
    caregiver_need_1_text: "Weten dat taal, gewoonten en vertrouwde omgang onderdeel zijn van de aandacht voor uw naaste.",
    caregiver_need_2_title: "Adempauze",
    caregiver_need_2_text: "Tijd voor rust, werk, gezin of herstel, terwijl uw naaste een betekenisvolle dag heeft.",
    caregiver_need_3_title: "Meedenken",
    caregiver_need_3_text: "Uw ervaring helpt om de behoefte in Winterswijk en omgeving zorgvuldig in beeld te brengen.",
    caregiver_cta_title: "Wilt u uw behoefte of belangstelling delen?",
    caregiver_cta_text: "Dit is nog geen formele aanmelding. Uw reactie helpt om de dagbesteding zorgvuldig op te bouwen."
  },
  campaign: {
    title: "Help deze plek mogelijk maken",
    description: "Uw bijdrage ondersteunt de voorbereiding en opstart van cultureel passende dagbesteding.",
    target_amount: 25000,
    current_amount: 0,
    published: true
  },
  events: []
};

const formatEuro = (value) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(value || 0));

const HERO_TITLE_SIZE_OPTIONS = [
  ["compact", "Compact"],
  ["normal", "Normaal"],
  ["large", "Groot"]
];

const HERO_TITLE_SIZE_CLASSES = {
  compact: {
    desktop: "text-[1.9rem] sm:text-4xl lg:text-5xl",
    mobile: "text-[1.55rem]"
  },
  normal: {
    desktop: "text-[2.15rem] sm:text-5xl lg:text-6xl",
    mobile: "text-[1.72rem]"
  },
  large: {
    desktop: "text-[2.35rem] sm:text-6xl lg:text-7xl",
    mobile: "text-[1.95rem]"
  }
};

const getHeroTitleSize = (value) => HERO_TITLE_SIZE_CLASSES[value] || HERO_TITLE_SIZE_CLASSES.normal;

const SiteDataContext = createContext(DEFAULT_SITE_DATA);
const SiteDataActionsContext = createContext({
  setSiteData: () => {},
  refreshSiteData: async () => DEFAULT_SITE_DATA
});

const mergeSiteData = (data = {}) => {
  const content = { ...DEFAULT_SITE_DATA.content, ...(data.content || {}) };
  return {
    content,
    campaign: { ...DEFAULT_SITE_DATA.campaign, ...(data.campaign || {}) },
    events: data.events || []
  };
};

const SiteDataProvider = ({ children }) => {
  const [siteData, setSiteData] = useState(DEFAULT_SITE_DATA);

  const refreshSiteData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/site`);
      const mergedData = mergeSiteData(response.data);
      setSiteData(mergedData);
      return mergedData;
    } catch {
      setSiteData(DEFAULT_SITE_DATA);
      return DEFAULT_SITE_DATA;
    }
  }, []);

  useEffect(() => {
    refreshSiteData();
  }, [refreshSiteData]);

  return <SiteDataContext.Provider value={siteData}>
    <SiteDataActionsContext.Provider value={{ setSiteData, refreshSiteData }}>
      {children}
    </SiteDataActionsContext.Provider>
  </SiteDataContext.Provider>;
};

const useSiteData = () => {
  const siteData = useContext(SiteDataContext);
  return siteData;
};

const useSiteDataActions = () => useContext(SiteDataActionsContext);

const Logo = () => (
  <span className="wordmark" aria-label="Rumah Kasih">
    <span className="wordmark-rumah">Rumah</span>
    <span className="wordmark-kasih" aria-hidden="true">
      <span>K</span><img src={handsMark} alt="" /><span>SIH</span>
    </span>
  </span>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const links = [
    ["Home", "/"],
    ["Dagbesteding", "/dagbesteding"],
    ["Mantelzorgers", "/mantelzorgers"],
    ["Kennis & Nieuws", "/kennis-en-nieuws"],
    ["Agenda", "/agenda"],
    ["Steun ons", "/steun-ons"],
    ["Contact", "/contact"]
  ];
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-primary/95 text-primary-foreground backdrop-blur">
      <nav className="editorial-container flex h-20 items-center justify-between">
        <Link to="/" aria-label="Naar de startpagina" title="Home" className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"><Logo /></Link>
        <div className="hidden items-center gap-7 lg:flex">
          {links.map(([label, href]) => <NavLink key={href} to={href} end={href === "/"} className={({ isActive }) => `border-b-2 py-2 text-sm font-semibold transition-colors ${isActive ? "border-accent text-white" : "border-transparent text-white/85 hover:border-white/40 hover:text-white"}`}>{label}</NavLink>)}
        </div>
        <div className="flex items-center gap-2">
          <Button asChild className="hidden rounded-md bg-accent hover:bg-accent/90 sm:inline-flex">
            <Link to="/steun-ons"><Heart className="mr-2 h-4 w-4" />Help mee</Link>
          </Button>
          <button className="p-2 text-white lg:hidden" onClick={() => setOpen(!open)} aria-label={open ? "Sluit menu" : "Open menu"}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>
      {open && <div className="border-t border-white/10 bg-primary px-4 py-4 lg:hidden">
        {links.map(([label, href]) => <NavLink key={href} to={href} end={href === "/"} onClick={() => setOpen(false)} className={({ isActive }) => `block border-b border-white/10 py-3 font-semibold last:border-0 ${isActive ? "text-accent" : "text-white"}`}>{label}</NavLink>)}
      </div>}
    </header>
  );
};

const PageShell = ({ children }) => <><Navigation /><main>{children}</main><Footer /></>;

const Hero = ({ content }) => {
  const titleSize = getHeroTitleSize(content.hero_title_size);

  return <section
    className="hero relative flex min-h-[74vh] items-end overflow-hidden"
    style={{
      backgroundImage: `url("${content.hero_image_url || heroImage}")`,
      backgroundPosition: content.hero_image_position || "center"
    }}
  >
    <div className="hero-overlay absolute inset-0 bg-gradient-to-r from-primary/90 via-primary/62 to-black/18" />
    <div className="hero-desktop-content editorial-container relative flex min-h-[inherit] w-full items-end pb-16 pt-28 text-white md:pb-24">
      <div className="max-w-[20rem] sm:max-w-3xl lg:max-w-5xl">
        <p className="mb-5 flex items-center gap-2 text-sm font-bold uppercase text-white/90">
          <MapPin className="h-4 w-4" /> Winterswijk
        </p>
        <h1 className={`text-balance font-heading font-bold leading-[1.06] ${titleSize.desktop}`}>
          {content.hero_title}
        </h1>
        <p className="mt-5 max-w-3xl text-base leading-relaxed text-white/92 sm:text-xl">
          {content.hero_text}
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button asChild size="lg" className="rounded-full bg-accent px-7 hover:bg-accent/90">
            <Link to="/contact">Neem contact op <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-white bg-transparent px-7 text-white hover:bg-white hover:text-foreground">
            <Link to="/steun-ons">Steun de opstart</Link>
          </Button>
        </div>
      </div>
    </div>
    <div className="hero-mobile-layout">
      <div className="hero-mobile-frame">
        <img src={content.hero_image_url || heroImage} alt="" />
        <div className="hero-mobile-frame-overlay" />
        <div className="hero-mobile-title">
          <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase text-white/90">
            <MapPin className="h-4 w-4" /> Winterswijk
          </p>
          <h1 className={`text-balance font-heading font-bold leading-[1.06] ${titleSize.mobile}`}>
            {content.hero_title}
          </h1>
        </div>
      </div>
      <div className="hero-mobile-copy">
        <p>{content.hero_text}</p>
        <div className="mt-6 flex flex-col gap-3">
          <Button asChild size="lg" className="rounded-full bg-accent px-7 hover:bg-accent/90">
            <Link to="/contact">Neem contact op <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-white bg-transparent px-7 text-white hover:bg-white hover:text-foreground">
            <Link to="/steun-ons">Steun de opstart</Link>
          </Button>
        </div>
      </div>
    </div>
  </section>;
};

const PageIntro = ({ label, title, children, action }) => (
  <section className="editorial-section">
    <div className="reading-container">
      <p className="section-label">{label}</p>
      <h1 className="section-title">{title}</h1>
      <div className="editorial-lead mt-6 space-y-4">{children}</div>
      {action && <div className="mt-8">{action}</div>}
    </div>
  </section>
);

const WhyPreview = ({ content }) => (
  <section className="editorial-section">
    <div className="editorial-container grid gap-16 lg:grid-cols-[minmax(0,.9fr)_minmax(0,1.1fr)]">
      <div>
        <p className="section-label">Waarom dit nodig is</p>
        <h2 className="section-title">{content.home_intro_title}</h2>
        <p className="editorial-lead mt-7">
          {content.home_intro_text}
        </p>
        <Button asChild variant="link" className="mt-7 px-0 text-primary"><Link to="/dagbesteding">Lees over de dagbesteding <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
      </div>
      <div className="space-y-8 border-l border-border pl-8">
        {[
          [Users, "Voor ouderen", "Een veilige dag met ritme en vertrouwde culturele aanknopingspunten."],
          [HandHeart, "Voor mantelzorgers", "Tijd om op adem te komen, met vertrouwen in de plek waar hun naaste verblijft."],
          [ShieldCheck, "Met kwaliteit", "Zorginhoud, veiligheid en professionele samenwerking worden zorgvuldig opgebouwd."],
          [Clock3, "Iedere dinsdag", "De dagbesteding vindt plaats van 10.00 tot 13.00 uur."]
        ].map(([Icon, title, text]) => <article key={title} className="grid gap-5 sm:grid-cols-[2rem_1fr]">
          <Icon className="mt-1 h-6 w-6 text-accent" />
          <div>
            <h3 className="font-heading text-2xl font-semibold leading-tight">{title}</h3>
            <p className="mt-2 max-w-xl leading-relaxed text-muted-foreground">{text}</p>
          </div>
        </article>)}
      </div>
    </div>
  </section>
);

const ApproachPreview = ({ content }) => (
  <section className="editorial-section bg-background">
    <div className="editorial-container">
      <div className="grid gap-8 rounded-2xl bg-primary p-6 text-primary-foreground md:grid-cols-[minmax(0,1fr)_auto] md:items-end md:p-10">
        <div className="max-w-4xl">
          <p className="text-sm font-bold uppercase tracking-wide text-accent">{content.approach_label}</p>
          <h2 className="mt-3 font-heading text-4xl font-bold leading-tight sm:text-5xl">{content.approach_title}</h2>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-white/80">{content.approach_text}</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row md:flex-col">
          <Button asChild className="rounded-full bg-accent hover:bg-accent/90"><Link to="/steun-ons">Bekijk hoe u kunt helpen</Link></Button>
          <Button asChild variant="outline" className="rounded-full border-white bg-transparent text-white hover:bg-white hover:text-primary"><Link to="/contact">Samenwerking bespreken</Link></Button>
        </div>
      </div>
    </div>
  </section>
);

const CampaignProgress = ({ campaign, compact = false }) => {
  const target = Number(campaign.target_amount || 0);
  const current = Number(campaign.current_amount || 0);
  const percentage = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  if (!campaign.published) return null;
  if (compact) {
    return (
      <div className="home-highlight-panel">
        <p className="section-label">Crowdfunding</p>
        <h2 className="mt-3 font-heading text-3xl font-bold leading-tight md:text-4xl">{campaign.title}</h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">{campaign.description}</p>
        <div className="mt-7">
          <div className="flex items-end justify-between gap-4">
            <div><p className="text-sm text-muted-foreground">Opgehaald</p><p className="text-2xl font-bold text-primary">{formatEuro(current)}</p></div>
            <div className="text-right"><p className="text-sm text-muted-foreground">Doel</p><p className="text-lg font-semibold">{formatEuro(target)}</p></div>
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-secondary">
            <div className="h-full rounded-full bg-accent" style={{ width: `${percentage}%` }} />
          </div>
          <p className="mt-3 text-sm font-semibold text-primary">{percentage}% van het doel bereikt</p>
          <Button asChild className="mt-5 rounded-full bg-primary hover:bg-primary/90"><Link to="/steun-ons">Steun de opstart</Link></Button>
        </div>
      </div>
    );
  }
  return (
    <section className="editorial-section">
      <div className="editorial-container">
        <div className="grid gap-12 border-y border-border py-10 md:grid-cols-[1fr_.8fr] md:py-14">
          <div>
            <p className="section-label">Crowdfunding</p>
            <h2 className="mt-3 font-heading text-4xl font-bold leading-tight md:text-5xl">{campaign.title}</h2>
            <p className="editorial-lead mt-5">{campaign.description}</p>
          </div>
          <div>
            <div className="flex items-end justify-between gap-4">
              <div><p className="text-sm text-muted-foreground">Opgehaald</p><p className="text-3xl font-bold text-primary">{formatEuro(current)}</p></div>
              <div className="text-right"><p className="text-sm text-muted-foreground">Doel</p><p className="text-xl font-semibold">{formatEuro(target)}</p></div>
            </div>
            <div className="mt-6 h-3 overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-accent" style={{ width: `${percentage}%` }} />
            </div>
            <p className="mt-3 text-sm font-semibold text-primary">{percentage}% van het doel bereikt</p>
            <Button asChild className="mt-6 rounded-full bg-primary hover:bg-primary/90"><Link to="/steun-ons">Steun de opstart</Link></Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const EventsPreview = ({ events, content }) => (
  <section className="editorial-section">
    <div className="editorial-container">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="section-label">Agenda</p>
          <h2 className="section-title">{content.agenda_title}</h2>
        </div>
        <Button asChild variant="link" className="px-0 text-primary"><Link to="/agenda">Bekijk agenda <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
      </div>
      <EventList events={events.slice(0, 3)} emptyText="Er zijn nog geen gepubliceerde bijeenkomsten." />
    </div>
  </section>
);

const EventList = ({ events, emptyText }) => (
  <div className="mt-10 divide-y divide-border border-y border-border">
    {events.length === 0 ? <p className="py-7 text-muted-foreground">{emptyText}</p> : events.map(event => (
      <article key={event.id || `${event.title}-${event.date}`} className="py-7">
        <CalendarDays className="h-6 w-6 text-accent" />
        <p className="mt-4 text-sm font-bold uppercase text-primary">{event.date}{event.time ? ` · ${event.time}` : ""}</p>
        <h3 className="mt-2 font-heading text-2xl font-semibold leading-tight">{event.title}</h3>
        {event.location && <p className="mt-2 flex gap-2 text-sm text-muted-foreground"><MapPin className="h-4 w-4" />{event.location}</p>}
        <p className="mt-3 max-w-3xl leading-relaxed text-muted-foreground">{event.description}</p>
      </article>
    ))}
  </div>
);

const Home = () => {
  const siteData = useSiteData();
  return (
    <PageShell>
      <Hero content={siteData.content} />
      <WhyPreview content={siteData.content} />
      <section className="home-highlights bg-secondary/35">
        <div className="editorial-container grid gap-10 lg:grid-cols-[minmax(0,.95fr)_minmax(0,1fr)]">
          <CampaignProgress campaign={siteData.campaign} compact />
          <KnowledgeHomeBlocks api={API} compact />
        </div>
      </section>
      <EventsPreview events={siteData.events} content={siteData.content} />
    </PageShell>
  );
};

const DayCarePage = () => {
  const siteData = useSiteData();
  return (
    <PageShell>
      <ErrorBoundary>
        <DayCareOverview content={siteData.content} />
      </ErrorBoundary>
      <FAQ />
    </PageShell>
  );
};

const CaregiversPage = () => {
  const siteData = useSiteData();
  return (
    <PageShell>
      <ErrorBoundary>
        <CaregiverOverview content={siteData.content} />
      </ErrorBoundary>
    </PageShell>
  );
};

const AgendaPage = () => {
  const siteData = useSiteData();
  return (
    <PageShell>
      <PageIntro label="Agenda" title={siteData.content.agenda_title}>
        <p>{siteData.content.agenda_intro}</p>
      </PageIntro>
      <section className="pb-16 md:pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <EventList events={siteData.events} emptyText="Er zijn nog geen gepubliceerde bijeenkomsten." />
        </div>
      </section>
    </PageShell>
  );
};

const SupportPage = () => {
  const siteData = useSiteData();
  const [loading, setLoading] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState("medium");
  const donate = async (packageId) => {
    setLoading(packageId);
    try {
      const { data } = await axios.post(`${API}/donation/checkout`, { package_id: packageId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error("Doneren is op dit moment nog niet beschikbaar. Neem gerust contact met ons op.");
      setLoading(null);
    }
  };
  const packages = [["small", 10], ["medium", 25], ["large", 50], ["generous", 100]];
  const selectedAmount = packages.find(([id]) => id === selectedPackage)?.[1] || 25;
  return (
    <PageShell>
      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[minmax(0,.9fr)_minmax(24rem,.75fr)] lg:px-8">
          <div>
            <p className="section-label">Steun ons</p>
            <h1 className="section-title">Steun de opstart</h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">{siteData.content.support_text}</p>
            {siteData.campaign.published && <div className="mt-8 max-w-2xl border-y border-border py-6">
              <div className="flex items-end justify-between gap-4">
                <div><p className="text-sm text-muted-foreground">Opgehaald</p><p className="text-3xl font-bold text-primary">{formatEuro(siteData.campaign.current_amount)}</p></div>
                <div className="text-right"><p className="text-sm text-muted-foreground">Doel</p><p className="text-xl font-semibold">{formatEuro(siteData.campaign.target_amount)}</p></div>
              </div>
              <div className="mt-5 h-3 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-accent" style={{ width: `${siteData.campaign.target_amount > 0 ? Math.min(100, Math.round((Number(siteData.campaign.current_amount || 0) / Number(siteData.campaign.target_amount || 1)) * 100)) : 0}%` }} />
              </div>
              <p className="mt-3 text-sm font-semibold text-primary">{siteData.campaign.target_amount > 0 ? Math.min(100, Math.round((Number(siteData.campaign.current_amount || 0) / Number(siteData.campaign.target_amount || 1)) * 100)) : 0}% van het doel bereikt</p>
            </div>}
            <div className="mt-7 space-y-3">
              {[siteData.content.support_item_1, siteData.content.support_item_2, siteData.content.support_item_3].map(item => <div key={item} className="flex gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" /><span>{item}</span></div>)}
            </div>
            <p className="mt-7 text-sm text-muted-foreground">Fondsen, zorgorganisaties, gemeente, ondernemers en vrijwilligers kunnen ook bijdragen via kennis, netwerk, huisvesting of samenwerking.</p>
          </div>
          <div className="self-start border border-border bg-secondary/25 p-6 sm:p-8">
            <p className="section-label">Eenmalige bijdrage</p>
            <h2 className="mt-2 text-3xl font-semibold">Kies een bedrag</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">Elke bijdrage helpt bij een duurzame en herkenbare dagbesteding in Winterswijk.</p>
            <div className="mt-7 grid grid-cols-2 gap-3" role="group" aria-label="Kies een donatiebedrag">
              {packages.map(([id, amount]) => {
                const selected = selectedPackage === id;
                return <button key={id} type="button" aria-pressed={selected} onClick={() => setSelectedPackage(id)} disabled={!!loading} className={`relative flex h-16 items-center justify-center rounded-md border-2 bg-white text-lg font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 ${selected ? "border-accent text-primary" : "border-border text-foreground hover:border-primary/50"}`}>
                  {formatEuro(amount)}
                  {selected && <CheckCircle2 className="absolute right-3 h-5 w-5 text-accent" aria-hidden="true" />}
                </button>;
              })}
            </div>
            <Button onClick={() => donate(selectedPackage)} disabled={!!loading} className="mt-5 h-12 w-full rounded-md bg-accent text-base hover:bg-accent/90">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Ga verder met {formatEuro(selectedAmount)} <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
            <p className="mt-4 flex items-start gap-2 text-sm leading-relaxed text-muted-foreground"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />Veilige betaling via iDEAL of kaart zodra de betaalomgeving actief is.</p>
            <div className="mt-7 border-t border-border pt-6">
              <p className="text-sm font-semibold text-foreground">Wilt u als organisatie bijdragen?</p>
              <Button asChild variant="link" className="mt-1 h-auto px-0 py-1 text-primary"><Link to="/contact">Bespreek een samenwerking <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

const FAQ = () => {
  const items = [
    ["Wanneer is de dagbesteding geopend?", "De dagbesteding vindt iedere dinsdag plaats van 10.00 tot 13.00 uur. Dinsdag 14 juli 2026 is de laatste bijeenkomst voor de zomerstop. Daarna is Rumah Kasih zes weken gesloten; actuele data publiceren we in de agenda."],
    ["Voor wie is Rumah Kasih bedoeld?", "De dagbesteding is er voor Molukse en Indische ouderen en voor ouderen met affiniteit met deze gemeenschappen, bijvoorbeeld door hun geboorte, levensgeschiedenis of culturele verbondenheid. Er is extra aandacht voor dementie, Alzheimer en andere hersenaandoeningen, en voor de mantelzorgers die hen ondersteunen."],
    ["Kan ik mijn naaste al aanmelden?", "U kunt uw belangstelling doorgeven via het contactformulier. Dit is nog geen formele aanmelding, maar helpt ons de behoefte goed in beeld te brengen."],
    ["Hoe kan mijn organisatie bijdragen?", "We spreken graag met zorgaanbieders, gemeente, fondsen, ondernemers en maatschappelijke organisaties over deskundigheid, financiering, huisvesting en samenwerking."],
    ["Waar worden donaties aan besteed?", "Donaties ondersteunen de voorbereiding en opstart. Een definitief campagnebedrag en een uitgesplitste begroting worden gepubliceerd zodra deze zijn vastgesteld."]
  ];
  return <section className="bg-secondary/25 py-14 md:py-20" id="faq"><div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,.7fr)_minmax(0,1.3fr)] lg:gap-20 lg:px-8">
    <div className="max-w-md">
      <p className="section-label">Veelgestelde vragen</p>
      <h2 className="section-title">Goed om te weten</h2>
      <p className="mt-5 text-lg leading-relaxed text-muted-foreground">Hier vindt u praktische informatie over de dagbesteding, aanmelden en bijdragen. Staat uw vraag er niet tussen? Neem dan gerust contact met ons op.</p>
      <Button asChild variant="outline" className="mt-7 rounded-md border-primary text-primary hover:bg-primary hover:text-white"><Link to="/contact">Stel uw vraag <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
    </div>
    <Accordion type="single" collapsible defaultValue="item-0" className="border-y border-border">{items.map(([q, a], i) => <AccordionItem key={q} value={`item-${i}`}><AccordionTrigger className="py-5 text-left text-lg font-semibold hover:text-primary">{q}</AccordionTrigger><AccordionContent className="max-w-3xl pb-5 pr-10 text-base leading-relaxed text-muted-foreground">{a}</AccordionContent></AccordionItem>)}</Accordion>
  </div></section>;
};

const ContactPage = () => {
  const siteData = useSiteData();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const submit = async (event) => {
    event.preventDefault(); setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message); setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Versturen lukt nu niet. Probeer het later opnieuw."); }
    finally { setSending(false); }
  };
  return <PageShell><section className="bg-secondary/40 py-14 md:py-20"><div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[.8fr_1.2fr] lg:px-8">
    <div><p className="section-label">Contact</p><h1 className="section-title">{siteData.content.contact_title}</h1><p className="mt-5 text-lg leading-relaxed text-muted-foreground">{siteData.content.contact_text}</p>
      <div className="mt-8 space-y-4"><div className="flex gap-3"><MapPin className="h-5 w-5 text-accent" /><span>Winterswijk en omgeving</span></div><div className="flex gap-3"><MessageCircle className="h-5 w-5 text-accent" /><span>Gebruik het formulier voor belangstelling, vragen en samenwerking.</span></div></div>
    </div>
    <form onSubmit={submit} className="grid gap-4 bg-white p-6 shadow-sm sm:grid-cols-2 sm:p-8">
      <label className="text-sm font-semibold">Naam *<Input required value={form.name} onChange={e => setForm({...form, name:e.target.value})} className="mt-2 h-11 rounded-md" /></label>
      <label className="text-sm font-semibold">E-mail *<Input type="email" required value={form.email} onChange={e => setForm({...form, email:e.target.value})} className="mt-2 h-11 rounded-md" /></label>
      <label className="text-sm font-semibold sm:col-span-2">Onderwerp *<Input required value={form.subject} onChange={e => setForm({...form, subject:e.target.value})} placeholder="Bijvoorbeeld: belangstelling of samenwerking" className="mt-2 h-11 rounded-md" /></label>
      <label className="text-sm font-semibold sm:col-span-2">Bericht *<Textarea required rows={5} value={form.message} onChange={e => setForm({...form, message:e.target.value})} className="mt-2 rounded-md" /></label>
      <Button disabled={sending} className="rounded-md bg-accent hover:bg-accent/90 sm:col-span-2">{sending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verstuur bericht"}</Button>
    </form>
  </div></section></PageShell>;
};

const CONTENT_SECTIONS = [
  {
    title: "Home",
    fields: [
      ["hero_title", "Hero titel", "textarea"],
      ["hero_title_size", "Hero titelgrootte", "select", HERO_TITLE_SIZE_OPTIONS],
      ["hero_text", "Hero tekst", "textarea"],
      ["hero_image_url", "Hero afbeelding URL", "input"],
      ["hero_image_position", "Hero afbeelding positie", "input"],
      ["home_intro_title", "Intro titel", "input"],
      ["home_intro_text", "Intro tekst", "textarea"],
      ["approach_label", "Aanpak label", "input"],
      ["approach_title", "Aanpak titel", "input"],
      ["approach_text", "Aanpak tekst", "textarea"],
      ["footer_text", "Footer tekst", "textarea"]
    ]
  },
  {
    title: "Dagbesteding",
    fields: [
      ["daycare_label", "Label", "input"],
      ["daycare_title", "Titel", "input"],
      ["daycare_intro_1", "Intro alinea 1", "textarea"],
      ["daycare_intro_2", "Intro alinea 2", "textarea"],
      ["daycare_intro_3", "Intro alinea 3", "textarea"],
      ["daycare_image_url", "Afbeelding URL", "input"],
      ["daycare_image_alt", "Afbeelding alt-tekst", "input"],
      ["daycare_features_label", "Kenmerken label", "input"],
      ["daycare_features_title", "Kenmerken titel", "input"],
      ["daycare_feature_1_title", "Kenmerk 1 titel", "input"],
      ["daycare_feature_1_text", "Kenmerk 1 tekst", "textarea"],
      ["daycare_feature_2_title", "Kenmerk 2 titel", "input"],
      ["daycare_feature_2_text", "Kenmerk 2 tekst", "textarea"],
      ["daycare_feature_3_title", "Kenmerk 3 titel", "input"],
      ["daycare_feature_3_text", "Kenmerk 3 tekst", "textarea"],
      ["daycare_status_label", "Status label", "input"],
      ["daycare_status_title", "Status titel", "input"],
      ["daycare_status_text", "Status tekst", "textarea"]
    ]
  },
  {
    title: "Mantelzorgers",
    fields: [
      ["caregiver_label", "Label", "input"],
      ["caregiver_title", "Titel", "input"],
      ["caregiver_intro_1", "Intro alinea 1", "textarea"],
      ["caregiver_intro_2", "Intro alinea 2", "textarea"],
      ["caregiver_image_url", "Afbeelding URL", "input"],
      ["caregiver_image_alt", "Afbeelding alt-tekst", "input"],
      ["caregiver_needs_label", "Behoeften label", "input"],
      ["caregiver_needs_title", "Behoeften titel", "input"],
      ["caregiver_need_1_title", "Blok 1 titel", "input"],
      ["caregiver_need_1_text", "Blok 1 tekst", "textarea"],
      ["caregiver_need_2_title", "Blok 2 titel", "input"],
      ["caregiver_need_2_text", "Blok 2 tekst", "textarea"],
      ["caregiver_need_3_title", "Blok 3 titel", "input"],
      ["caregiver_need_3_text", "Blok 3 tekst", "textarea"],
      ["caregiver_cta_title", "CTA titel", "input"],
      ["caregiver_cta_text", "CTA tekst", "textarea"]
    ]
  },
  {
    title: "Agenda, steun en contact",
    fields: [
      ["agenda_title", "Agenda titel", "input"],
      ["agenda_intro", "Agenda intro", "textarea"],
      ["support_title", "Steun titel", "input"],
      ["support_text", "Steun tekst", "textarea"],
      ["support_item_1", "Steun punt 1", "input"],
      ["support_item_2", "Steun punt 2", "input"],
      ["support_item_3", "Steun punt 3", "input"],
      ["contact_title", "Contact titel", "input"],
      ["contact_text", "Contact tekst", "textarea"]
    ]
  }
];

const INTERNAL_TYPES = [
  ["meeting", "Vergaderingen"],
  ["action", "Actiepunten"],
  ["decision", "Besluiten"],
  ["idea", "Brainstorm"],
  ["workgroup", "Werkgroepen"],
  ["document", "Documenten"],
  ["announcement", "Mededelingen"],
  ["user", "Gebruikers en rechten"]
];

const INTERNAL_TYPE_LABELS = Object.fromEntries(INTERNAL_TYPES);

const DEFAULT_INTERNAL_SPACES = [
  { id: "space-general", name: "Algemene omgeving", type: "general", visibility: "internal", members: [], invitees: [] },
  { id: "space-board", name: "Algemeen bestuur", type: "board", visibility: "restricted", members: [], invitees: [] }
];

const INTERNAL_SPACE_TYPE_LABELS = {
  general: "Algemeen",
  board: "Bestuur",
  workgroup: "Werkgroep"
};

const INTERNAL_VISIBILITY_LABELS = {
  internal: "Iedereen intern",
  restricted: "Afgeschermd voorbereid",
  workgroup: "Alleen werkgroep"
};

const INTERNAL_STATUS_OPTIONS = {
  meeting: [["planned", "Gepland"], ["done", "Afgerond"], ["cancelled", "Geannuleerd"]],
  action: [["open", "Open"], ["in_progress", "Bezig"], ["done", "Afgerond"], ["blocked", "Geblokkeerd"]],
  decision: [["proposed", "Voorgesteld"], ["approved", "Vastgesteld"], ["rejected", "Afgewezen"]],
  idea: [["new", "Nieuw"], ["discussing", "In bespreking"], ["accepted", "Overgenomen"], ["parked", "Geparkeerd"]],
  workgroup: [["active", "Actief"], ["paused", "Gepauzeerd"], ["closed", "Gesloten"]],
  document: [["draft", "Concept"], ["final", "Definitief"], ["archived", "Archief"]],
  announcement: [["draft", "Concept"], ["published", "Gepubliceerd"], ["archived", "Archief"]],
  user: [["active", "Actief"], ["inactive", "Inactief"]]
};

const createInternalForm = (type = "meeting") => ({
  type,
  space_id: "space-general",
  title: "",
  description: "",
  status: INTERNAL_STATUS_OPTIONS[type]?.[0]?.[0] || "open",
  owner: "",
  workgroup: "",
  location: "",
  date: "",
  deadline: "",
  url: "",
  participants: [],
  tags: []
});

const peopleToInput = (values) => Array.isArray(values) ? values.join(", ") : values || "";
const inputToList = (value) => String(value || "").split(",").map(item => item.trim()).filter(Boolean);
const isEmailLike = (value) => /\S+@\S+\.\S+/.test(String(value || ""));
const namesOnly = (values) => (Array.isArray(values) ? values : inputToList(values)).filter(value => !isEmailLike(value));

const monthLabel = (value) => value.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
const dateKey = (value) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
const buildCalendarDays = (monthDate) => {
  const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7;
  const start = new Date(firstOfMonth);
  start.setDate(firstOfMonth.getDate() - startOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
};

const InternalCalendar = ({ monthDate, meetings, onEdit, onMonthChange, onDateSelect }) => {
  const today = dateKey(new Date());
  const days = buildCalendarDays(monthDate);
  const meetingsByDate = meetings.reduce((accumulator, item) => {
    if (!item.date) return accumulator;
    accumulator[item.date] = [...(accumulator[item.date] || []), item];
    return accumulator;
  }, {});
  return <div className="overflow-hidden rounded-md border border-border bg-white">
    <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
      <div><p className="section-label">Agenda</p><h3 className="mt-1 font-sans text-xl font-bold capitalize">{monthLabel(monthDate)}</h3></div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="h-9 rounded-md px-3" onClick={() => onMonthChange(-1)}>Vorige</Button>
        <Button type="button" variant="outline" className="h-9 rounded-md px-3" onClick={() => onMonthChange(1)}>Volgende</Button>
      </div>
    </div>
    <div className="grid grid-cols-7 border-b border-border bg-secondary/50 text-center text-xs font-bold uppercase tracking-wide text-muted-foreground">
      {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map(day => <div key={day} className="p-2">{day}</div>)}
    </div>
    <div className="grid grid-cols-7">
      {days.map(day => {
        const key = dateKey(day);
        const isCurrentMonth = day.getMonth() === monthDate.getMonth();
        const dayMeetings = meetingsByDate[key] || [];
        return <div key={key} role="button" tabIndex={0} onClick={() => onDateSelect?.(key)} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") onDateSelect?.(key); }} className={`min-h-[6.5rem] cursor-pointer border-b border-r border-border p-2 transition-colors hover:bg-secondary/45 ${isCurrentMonth ? "bg-white" : "bg-secondary/25"} ${key === today ? "ring-2 ring-inset ring-accent" : ""}`}>
          <div className={`text-xs font-bold ${isCurrentMonth ? "text-primary" : "text-muted-foreground/60"}`}>{day.getDate()}</div>
          <div className="mt-2 grid gap-1">
            {dayMeetings.slice(0, 3).map(item => <button key={item.id} type="button" onClick={event => { event.stopPropagation(); onEdit(item); }} className="rounded-sm bg-primary px-2 py-1 text-left text-[0.72rem] font-semibold leading-tight text-white hover:bg-primary/90">{item.title}</button>)}
            {dayMeetings.length > 3 && <span className="text-[0.7rem] text-muted-foreground">+{dayMeetings.length - 3} meer</span>}
          </div>
        </div>;
      })}
    </div>
  </div>;
};

const AdminPage = () => {
  const { setSiteData } = useSiteDataActions();
  const [adminMode, setAdminMode] = useState("website");
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(DEFAULT_SITE_DATA);
  const [internalSpaces, setInternalSpaces] = useState(DEFAULT_INTERNAL_SPACES);
  const [selectedSpaceId, setSelectedSpaceId] = useState("space-general");
  const [internalItems, setInternalItems] = useState([]);
  const [internalSummary, setInternalSummary] = useState({ counts: {}, open_actions: [], upcoming_meetings: [], announcements: [] });
  const [internalType, setInternalType] = useState("meeting");
  const [internalForm, setInternalForm] = useState(createInternalForm("meeting"));
  const [editingInternalId, setEditingInternalId] = useState("");
  const [savingInternal, setSavingInternal] = useState(false);
  const [savingSpace, setSavingSpace] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [activeInternalModule, setActiveInternalModule] = useState("overview");

  const loadAdminData = useCallback(async () => {
    const response = await axios.get(`${API}/admin/site`, { withCredentials: true });
    setData(mergeSiteData(response.data));
  }, []);

  const loadInternalData = useCallback(async () => {
    const [spacesResponse, itemsResponse, summaryResponse] = await Promise.all([
      axios.get(`${API}/admin/internal/spaces`, { withCredentials: true }),
      axios.get(`${API}/admin/internal`, { withCredentials: true }),
      axios.get(`${API}/admin/internal/summary`, { withCredentials: true })
    ]);
    setInternalSpaces(spacesResponse.data?.items?.length ? spacesResponse.data.items : DEFAULT_INTERNAL_SPACES);
    setInternalItems(itemsResponse.data?.items ?? []);
    setInternalSummary(summaryResponse.data ?? { counts: {}, open_actions: [], upcoming_meetings: [], announcements: [] });
  }, []);

  useEffect(() => {
    axios.get(`${API}/admin/session`, { withCredentials: true })
      .then(async ({ data: session }) => {
        if (session?.authenticated) {
          await loadAdminData();
          await loadInternalData();
          setAuthenticated(true);
        }
      })
      .catch(() => setAuthenticated(false));
  }, [loadAdminData, loadInternalData]);

  const login = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
      setPassword("");
      await loadAdminData();
      await loadInternalData();
      setAuthenticated(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Inloggen lukt niet. Controleer het admin-wachtwoord.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site`, data, { withCredentials: true });
      const savedData = mergeSiteData(data);
      setData(savedData);
      setSiteData(savedData);
      toast.success("Website-inhoud is opgeslagen.");
    } catch (error) {
      if (error.response?.status === 401) setAuthenticated(false);
      toast.error(error.response?.data?.detail || "Opslaan lukt niet.");
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, null, { withCredentials: true });
    } finally {
      setAuthenticated(false);
      setData(DEFAULT_SITE_DATA);
    }
  };

  const updateContent = (key, value) => setData(prev => ({ ...prev, content: { ...prev.content, [key]: value } }));
  const updateCampaign = (key, value) => setData(prev => ({ ...prev, campaign: { ...prev.campaign, [key]: value } }));
  const updateEvent = (index, key, value) => setData(prev => ({
    ...prev,
    events: prev.events.map((event, eventIndex) => eventIndex === index ? { ...event, [key]: value } : event)
  }));
  const addEvent = () => setData(prev => ({ ...prev, events: [...prev.events, { title: "", date: "", time: "", location: "", description: "", published: true }] }));
  const removeEvent = (index) => setData(prev => ({ ...prev, events: prev.events.filter((_, eventIndex) => eventIndex !== index) }));

  const changeInternalType = (value) => {
    setInternalType(value);
    setEditingInternalId("");
    setInternalForm({ ...createInternalForm(value), space_id: selectedSpaceId });
  };

  const changeInternalSpace = (spaceId) => {
    setSelectedSpaceId(spaceId);
    setEditingInternalId("");
    setInternalForm(prev => ({ ...createInternalForm(internalType), space_id: spaceId, workgroup: internalSpaces.find(space => space.id === spaceId)?.type === "workgroup" ? internalSpaces.find(space => space.id === spaceId)?.name || "" : prev.workgroup }));
  };

  const updateInternalForm = (key, value) => setInternalForm(prev => ({ ...prev, [key]: value }));

  const editInternalItem = (item) => {
    setAdminMode("internal");
    setInternalType(item.type);
    setSelectedSpaceId(item.space_id || "space-general");
    setEditingInternalId(item.id);
    setInternalForm({
      type: item.type,
      space_id: item.space_id || "space-general",
      title: item.title ?? "",
      description: item.description ?? "",
      status: item.status ?? INTERNAL_STATUS_OPTIONS[item.type]?.[0]?.[0] ?? "open",
      owner: item.owner ?? "",
      workgroup: item.workgroup ?? "",
      location: item.location ?? "",
      date: item.date ?? "",
      deadline: item.deadline ?? "",
      url: item.url ?? "",
      participants: item.participants ?? [],
      tags: item.tags ?? []
    });
  };

  const saveInternalItem = async () => {
    setSavingInternal(true);
    try {
      const payload = {
        ...internalForm,
        type: internalType,
        space_id: internalForm.space_id || selectedSpaceId,
        participants: Array.isArray(internalForm.participants) ? internalForm.participants : inputToList(internalForm.participants),
        tags: Array.isArray(internalForm.tags) ? internalForm.tags : String(internalForm.tags || "").split(",").map(tag => tag.trim()).filter(Boolean)
      };
      if (editingInternalId) {
        await axios.put(`${API}/admin/internal/${editingInternalId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/internal`, payload, { withCredentials: true });
      }
      toast.success("Intern item is opgeslagen.");
      setEditingInternalId("");
      setInternalForm({ ...createInternalForm(internalType), space_id: selectedSpaceId });
      await loadInternalData();
    } catch (error) {
      if (error.response?.status === 401) setAuthenticated(false);
      toast.error(error.response?.data?.detail || "Intern item opslaan lukt niet.");
    } finally {
      setSavingInternal(false);
    }
  };

  const deleteInternalItem = async (itemId) => {
    setSavingInternal(true);
    try {
      await axios.delete(`${API}/admin/internal/${itemId}`, { withCredentials: true });
      toast.success("Intern item is gearchiveerd.");
      if (editingInternalId === itemId) {
        setEditingInternalId("");
        setInternalForm({ ...createInternalForm(internalType), space_id: selectedSpaceId });
      }
      await loadInternalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Archiveren lukt niet.");
    } finally {
      setSavingInternal(false);
    }
  };

  const updateInternalSpacePeople = async (spaceId, field, value) => {
    const space = internalSpaces.find(item => item.id === spaceId);
    if (!space) return;
    setSavingSpace(true);
    try {
      const payload = {
        name: space.name,
        description: space.description || "",
        type: space.type || "workgroup",
        visibility: space.visibility || "internal",
        members: field === "members" ? namesOnly(value) : namesOnly(space.members),
        invitees: field === "invitees" ? namesOnly(value) : namesOnly(space.invitees),
        order: space.order || 0
      };
      await axios.put(`${API}/admin/internal/spaces/${spaceId}`, payload, { withCredentials: true });
      await loadInternalData();
      toast.success("Omgeving is bijgewerkt.");
    } catch (error) {
      if (error.response?.status === 401) setAuthenticated(false);
      toast.error(error.response?.data?.detail || "Omgeving bijwerken lukt niet.");
    } finally {
      setSavingSpace(false);
    }
  };

  const selectedSpace = internalSpaces.find(space => space.id === selectedSpaceId) || internalSpaces[0] || DEFAULT_INTERNAL_SPACES[0];
  const scopedInternalItems = internalItems.filter(item => (item.space_id || "space-general") === selectedSpaceId);
  const scopedTypeItems = scopedInternalItems.filter(item => item.type === internalType);
  const scopedOpenActions = scopedInternalItems.filter(item => item.type === "action" && item.status !== "done").slice(0, 8);
  const scopedUpcomingMeetings = scopedInternalItems.filter(item => item.type === "meeting" && item.status === "planned").slice(0, 5);
  const scopedMeetings = scopedInternalItems.filter(item => item.type === "meeting" && item.status !== "cancelled");
  const scopedDocuments = scopedInternalItems.filter(item => item.type === "document").slice(0, 6);
  const scopedDecisions = scopedInternalItems.filter(item => item.type === "decision").slice(0, 6);
  const changeCalendarMonth = (offset) => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  const startMeetingOnDate = (date) => {
    setActiveInternalModule("meeting");
    setInternalType("meeting");
    setEditingInternalId("");
    setInternalForm({
      ...createInternalForm("meeting"),
      space_id: selectedSpaceId,
      date,
      workgroup: selectedSpace.type === "workgroup" ? selectedSpace.name : "",
      title: ""
    });
  };
  const internalModules = [
    ["overview", "Overzicht"],
    ["meeting", "Vergaderingen"],
    ["action", "Actiepunten"],
    ["decision", "Besluiten"],
    ["document", "Documenten"],
    ["idea", "Brainstorm"],
    ["workgroup", "Werkgroepen"],
    ["announcement", "Mededelingen"],
    ["people", "Personen"]
  ];
  const activateInternalModule = (moduleId) => {
    setActiveInternalModule(moduleId);
    if (INTERNAL_TYPE_LABELS[moduleId]) changeInternalType(moduleId);
  };

  if (!authenticated) {
    return <PageShell><section className="grid min-h-[60vh] place-items-center px-4 py-16">
      <form onSubmit={login} className="w-full max-w-md bg-white p-6 shadow-sm">
        <p className="section-label">Admin</p>
        <h1 className="mt-3 text-3xl font-bold">Website beheren</h1>
        <p className="mt-3 text-muted-foreground">Log in met het admin-wachtwoord uit de backend configuratie.</p>
        <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Admin-wachtwoord" className="mt-6 h-11 rounded-md" required />
        <Button disabled={loading} className="mt-4 w-full rounded-md bg-accent hover:bg-accent/90">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Inloggen"}</Button>
      </form>
    </section></PageShell>;
  }

  if (adminMode === "internal") {
    const activeTypeItems = scopedInternalItems.filter(item => item.type === internalType);
    const nextMeeting = scopedUpcomingMeetings[0];
    const nextAction = scopedOpenActions[0];
    const allPeople = Array.from(new Set(internalSpaces.flatMap(space => namesOnly(space.members)))).sort((a, b) => a.localeCompare(b));
    return <div className="min-h-screen bg-secondary/45 font-sans text-primary">
      <header className="sticky top-0 z-40 border-b border-primary/10 bg-primary text-white">
        <div className="mx-auto flex h-16 max-w-[92rem] items-center justify-between gap-4 px-4 sm:px-6">
          <button type="button" onClick={() => setAdminMode("website")} className="flex items-center gap-3 text-left">
            <Logo />
            <span className="hidden border-l border-white/20 pl-3 text-sm font-bold sm:block">Intern portaal</span>
          </button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => setAdminMode("website")} className="hidden rounded-md border-white/25 bg-transparent text-white hover:bg-white/10 sm:inline-flex">Websitebeheer</Button>
            <Button type="button" variant="outline" onClick={logout} className="rounded-md border-white/25 bg-transparent text-white hover:bg-white/10">Uitloggen</Button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[92rem] gap-0 lg:grid-cols-[17rem_1fr]">
        <aside className="border-b border-border bg-white px-4 py-4 lg:min-h-[calc(100vh-4rem)] lg:border-b-0 lg:border-r lg:px-5 lg:py-6">
          <p className="text-xs font-bold uppercase tracking-wide text-accent">Beheer</p>
          <nav className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-1">
            {internalModules.map(([moduleId, label]) => {
              const active = activeInternalModule === moduleId;
              return <button key={moduleId} type="button" onClick={() => activateInternalModule(moduleId)} className={`rounded-md px-3 py-2.5 text-left text-sm font-bold transition-colors ${active ? "bg-primary text-white" : "text-primary hover:bg-secondary"}`}>{label}</button>;
            })}
          </nav>
          <div className="mt-6 rounded-md bg-secondary/60 p-3 text-xs leading-relaxed text-muted-foreground">
            Geen medische gegevens, diagnoses, BSN-nummers of clientdossiers opslaan.
          </div>
        </aside>

        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 border-b border-border pb-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-accent">Interne samenwerking</p>
              <h1 className="mt-2 font-sans text-2xl font-bold tracking-tight text-primary sm:text-3xl">{internalModules.find(([id]) => id === activeInternalModule)?.[1] || "Overzicht"}</h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">{selectedSpace.name}: {selectedSpace.description}</p>
            </div>
            <div className="w-full max-w-md">
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Omgeving</label>
              <Select value={selectedSpaceId} onValueChange={changeInternalSpace}>
                <SelectTrigger className="mt-2 h-11 rounded-md bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{internalSpaces.map(space => <SelectItem key={space.id} value={space.id}>{space.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {activeInternalModule === "overview" && <div className="mt-6 grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
            <section className="grid gap-4 sm:grid-cols-2">
              <button type="button" onClick={() => activateInternalModule("action")} className="rounded-md bg-white p-5 text-left shadow-sm">
                <p className="text-sm font-bold text-muted-foreground">Mijn actiepunten</p>
                <p className="mt-3 text-3xl font-bold text-primary">{scopedOpenActions.length}</p>
                <p className="mt-2 text-sm text-muted-foreground">{nextAction?.title || "Geen open actiepunten in deze omgeving."}</p>
              </button>
              <button type="button" onClick={() => activateInternalModule("meeting")} className="rounded-md bg-white p-5 text-left shadow-sm">
                <p className="text-sm font-bold text-muted-foreground">Volgende vergadering</p>
                <p className="mt-3 text-xl font-bold text-primary">{nextMeeting?.date || "Nog niet gepland"}</p>
                <p className="mt-2 text-sm text-muted-foreground">{nextMeeting?.title || "Maak een vergadering aan voor deze omgeving."}</p>
              </button>
              <button type="button" onClick={() => activateInternalModule("document")} className="rounded-md bg-white p-5 text-left shadow-sm">
                <p className="text-sm font-bold text-muted-foreground">Documenten en notulen</p>
                <p className="mt-3 text-3xl font-bold text-primary">{scopedDocuments.length}</p>
                <p className="mt-2 text-sm text-muted-foreground">Gebruik links naar veilige documenten totdat upload-opslag is gekoppeld.</p>
              </button>
              <button type="button" onClick={() => activateInternalModule("decision")} className="rounded-md bg-white p-5 text-left shadow-sm">
                <p className="text-sm font-bold text-muted-foreground">Besluitenregister</p>
                <p className="mt-3 text-3xl font-bold text-primary">{scopedDecisions.length}</p>
                <p className="mt-2 text-sm text-muted-foreground">Besluiten blijven los vindbaar naast notulen.</p>
              </button>
            </section>
            <section className="rounded-md bg-white p-5 shadow-sm">
              <h2 className="text-lg font-bold">Recente items</h2>
              <div className="mt-4 grid gap-3">
                {scopedInternalItems.slice(0, 6).map(item => <button key={item.id} type="button" onClick={() => { activateInternalModule(item.type); editInternalItem(item); }} className="rounded-md border border-border p-3 text-left text-sm hover:border-primary/40">
                  <span className="block text-xs font-bold uppercase tracking-wide text-accent">{INTERNAL_TYPE_LABELS[item.type] || item.type}</span>
                  <span className="mt-1 block font-bold">{item.title}</span>
                </button>)}
                {scopedInternalItems.length === 0 && <p className="text-sm text-muted-foreground">Nog geen interne items in deze omgeving.</p>}
              </div>
            </section>
          </div>}

          {activeInternalModule === "meeting" && <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_24rem]">
            <InternalCalendar monthDate={calendarMonth} meetings={scopedMeetings} onEdit={editInternalItem} onMonthChange={changeCalendarMonth} onDateSelect={startMeetingOnDate} />
            <section className="rounded-md bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold">{editingInternalId ? "Vergadering bewerken" : "Nieuwe vergadering"}</h2>
                <Button type="button" onClick={() => startMeetingOnDate(dateKey(new Date()))} className="h-9 rounded-md bg-accent px-3 hover:bg-accent/90"><Plus className="mr-2 h-4 w-4" />Vandaag</Button>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">Klik op een datum in de kalender om direct een agenda-item voor die dag te maken.</p>
              <div className="mt-4 grid gap-3">
                <label className="text-sm font-semibold">Datum<Input type="date" value={internalForm.date} onChange={e => updateInternalForm("date", e.target.value)} className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold">Titel<Input value={internalForm.title} onChange={e => updateInternalForm("title", e.target.value)} placeholder="Bijv. Werkgroepoverleg fondsen" className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold">Locatie<Input value={internalForm.location} onChange={e => updateInternalForm("location", e.target.value)} placeholder="Bijv. Eelinkstraat 57 of online" className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold">Deelnemers<Input value={peopleToInput(internalForm.participants)} onChange={e => updateInternalForm("participants", e.target.value)} placeholder="Namen, komma-gescheiden" className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold">Agenda / notulen / PDF-link<Input value={internalForm.url} onChange={e => updateInternalForm("url", e.target.value)} placeholder="https://..." className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold">Notitie<Textarea rows={4} value={internalForm.description} onChange={e => updateInternalForm("description", e.target.value)} className="mt-2 rounded-md" /></label>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button type="button" onClick={saveInternalItem} disabled={savingInternal || !internalForm.title.trim() || !internalForm.date} className="rounded-md bg-accent hover:bg-accent/90">{savingInternal ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{editingInternalId ? "Bijwerken" : "Opslaan"}</Button>
                {editingInternalId && <Button type="button" variant="outline" onClick={() => startMeetingOnDate(internalForm.date || dateKey(new Date()))} className="rounded-md">Nieuw</Button>}
              </div>
              <div className="mt-6 border-t border-border pt-4">
                <h3 className="text-sm font-bold">Geplande vergaderingen</h3>
                <div className="mt-3 grid gap-2">{scopedMeetings.length ? scopedMeetings.map(item => <button key={item.id} type="button" onClick={() => editInternalItem(item)} className="rounded-md bg-secondary/60 p-3 text-left text-sm hover:bg-secondary"><strong>{item.date || "Geen datum"}</strong><br />{item.title}</button>) : <p className="text-sm text-muted-foreground">Nog geen vergaderingen.</p>}</div>
              </div>
            </section>
          </div>}

          {activeInternalModule === "workgroup" && <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {internalSpaces.map(space => <button key={space.id} type="button" onClick={() => changeInternalSpace(space.id)} className={`rounded-md border bg-white p-5 text-left shadow-sm ${selectedSpaceId === space.id ? "border-primary" : "border-border"}`}>
              <span className="text-xs font-bold uppercase tracking-wide text-accent">{INTERNAL_SPACE_TYPE_LABELS[space.type] || space.type}</span>
              <h2 className="mt-2 text-xl font-bold">{space.name}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{space.description}</p>
              <p className="mt-4 text-xs font-bold text-muted-foreground">Leden: {namesOnly(space.members).length ? namesOnly(space.members).join(", ") : "Nog niet ingevuld"}</p>
            </button>)}
            <section className="rounded-md border border-border bg-white p-5 shadow-sm md:col-span-2 xl:col-span-3">
              <h2 className="text-lg font-bold">Leden en uitnodigingen beheren</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="text-sm font-semibold">Leden<Input value={peopleToInput(namesOnly(selectedSpace.members))} onChange={e => setInternalSpaces(prev => prev.map(space => space.id === selectedSpaceId ? { ...space, members: namesOnly(e.target.value) } : space))} className="mt-2 rounded-md bg-white" /></label>
                <label className="text-sm font-semibold">Toe te voegen personen<Input value={peopleToInput(namesOnly(selectedSpace.invitees))} onChange={e => setInternalSpaces(prev => prev.map(space => space.id === selectedSpaceId ? { ...space, invitees: namesOnly(e.target.value) } : space))} placeholder="Alleen namen, geen e-mailadressen" className="mt-2 rounded-md bg-white" /></label>
              </div>
              <Button type="button" onClick={() => updateInternalSpacePeople(selectedSpaceId, "members", peopleToInput(selectedSpace.members))} disabled={savingSpace} className="mt-4 rounded-md bg-primary hover:bg-primary/90">{savingSpace ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Omgeving opslaan</Button>
            </section>
          </div>}

          {activeInternalModule === "people" && <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_1fr]">
            <section className="rounded-md bg-white p-5 shadow-sm">
              <h2 className="text-lg font-bold">Personen per omgeving</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Beheer hier wie bij welke omgeving of werkgroep hoort. Dit is nog geen volwaardig gebruikers- en rollenbeheer; echte loginrechten blijven server-side nodig.</p>
              <div className="mt-5 grid gap-4">
                {internalSpaces.map(space => <div key={space.id} className="rounded-md border border-border p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent">{INTERNAL_SPACE_TYPE_LABELS[space.type] || space.type}</p>
                  <h3 className="mt-1 font-bold">{space.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Leden: {namesOnly(space.members).length ? namesOnly(space.members).join(", ") : "Nog niet ingevuld"}</p>
                  {namesOnly(space.invitees).length > 0 && <p className="mt-1 text-sm text-muted-foreground">Toe te voegen: {namesOnly(space.invitees).join(", ")}</p>}
                </div>)}
              </div>
            </section>
            <section className="rounded-md bg-white p-5 shadow-sm">
              <h2 className="text-lg font-bold">Leden en uitnodigingen aanpassen</h2>
              <div className="mt-4">
                <label className="text-sm font-semibold">Omgeving
                  <Select value={selectedSpaceId} onValueChange={changeInternalSpace}>
                    <SelectTrigger className="mt-2 h-11 rounded-md bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{internalSpaces.map(space => <SelectItem key={space.id} value={space.id}>{space.name}</SelectItem>)}</SelectContent>
                  </Select>
                </label>
              </div>
              <div className="mt-4 grid gap-4">
                <label className="text-sm font-semibold">Leden<Input value={peopleToInput(namesOnly(selectedSpace.members))} onChange={e => setInternalSpaces(prev => prev.map(space => space.id === selectedSpaceId ? { ...space, members: namesOnly(e.target.value) } : space))} placeholder="Bijv. Judith, Ronny" className="mt-2 rounded-md bg-white" /></label>
                <label className="text-sm font-semibold">Toe te voegen personen<Input value={peopleToInput(namesOnly(selectedSpace.invitees))} onChange={e => setInternalSpaces(prev => prev.map(space => space.id === selectedSpaceId ? { ...space, invitees: namesOnly(e.target.value) } : space))} placeholder="Alleen namen, geen e-mailadressen" className="mt-2 rounded-md bg-white" /></label>
              </div>
              <Button type="button" onClick={() => updateInternalSpacePeople(selectedSpaceId, "members", peopleToInput(selectedSpace.members))} disabled={savingSpace} className="mt-4 rounded-md bg-primary hover:bg-primary/90">{savingSpace ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Opslaan</Button>
              <div className="mt-6 rounded-md bg-secondary/60 p-4">
                <p className="text-sm font-bold">Alle bekende personen</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{allPeople.length ? allPeople.join(", ") : "Nog geen personen toegevoegd."}</p>
              </div>
            </section>
          </div>}

          {activeInternalModule !== "overview" && activeInternalModule !== "meeting" && activeInternalModule !== "workgroup" && activeInternalModule !== "people" && <div className="mt-6 grid gap-6 xl:grid-cols-[.9fr_1.1fr]">
            <section className="rounded-md bg-white p-5 shadow-sm">
              <h2 className="text-lg font-bold">{editingInternalId ? `${INTERNAL_TYPE_LABELS[internalType]} bewerken` : `Nieuw: ${INTERNAL_TYPE_LABELS[internalType]}`}</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-semibold">Status
                  <Select value={internalForm.status} onValueChange={value => updateInternalForm("status", value)}>
                    <SelectTrigger className="mt-2 h-11 rounded-md"><SelectValue /></SelectTrigger>
                    <SelectContent>{(INTERNAL_STATUS_OPTIONS[internalType] || []).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                  </Select>
                </label>
                <label className="text-sm font-semibold">Datum<Input type="date" value={internalForm.date} onChange={e => updateInternalForm("date", e.target.value)} className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold sm:col-span-2">Titel<Input value={internalForm.title} onChange={e => updateInternalForm("title", e.target.value)} className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold sm:col-span-2">Omschrijving<Textarea rows={5} value={internalForm.description} onChange={e => updateInternalForm("description", e.target.value)} className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold">Eigenaar<Input value={internalForm.owner} onChange={e => updateInternalForm("owner", e.target.value)} className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold">Deadline<Input type="date" value={internalForm.deadline} onChange={e => updateInternalForm("deadline", e.target.value)} className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold sm:col-span-2">Document- of vergaderlink<Input value={internalForm.url} onChange={e => updateInternalForm("url", e.target.value)} placeholder="https://..." className="mt-2 rounded-md" /></label>
                <label className="text-sm font-semibold sm:col-span-2">Betrokkenen<Input value={peopleToInput(internalForm.participants)} onChange={e => updateInternalForm("participants", e.target.value)} className="mt-2 rounded-md" /></label>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button type="button" onClick={saveInternalItem} disabled={savingInternal || !internalForm.title.trim()} className="rounded-md bg-accent hover:bg-accent/90">{savingInternal ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{editingInternalId ? "Bijwerken" : "Opslaan"}</Button>
                {editingInternalId && <Button type="button" variant="outline" onClick={() => { setEditingInternalId(""); setInternalForm({ ...createInternalForm(internalType), space_id: selectedSpaceId }); }} className="rounded-md">Nieuw</Button>}
              </div>
            </section>
            <section className="rounded-md bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold">{INTERNAL_TYPE_LABELS[internalType]}</h2>
                <span className="text-sm text-muted-foreground">{activeTypeItems.length} items</span>
              </div>
              <div className="mt-4 grid gap-3">
                {activeTypeItems.length === 0 && <p className="text-sm text-muted-foreground">Nog niets opgeslagen voor dit onderdeel.</p>}
                {activeTypeItems.map(item => <article key={item.id} className="rounded-md border border-border p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent">{item.status}</p>
                  <h3 className="mt-1 font-bold">{item.title}</h3>
                  {item.description && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" onClick={() => editInternalItem(item)} className="h-9 rounded-md">Bewerk</Button>
                    <Button type="button" variant="outline" onClick={() => deleteInternalItem(item.id)} className="h-9 rounded-md text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </article>)}
              </div>
            </section>
          </div>}
        </main>
      </div>
    </div>;
  }

  return <PageShell><section className="bg-secondary/40 py-12 md:py-16">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div><p className="section-label">Admin</p><h1 className="section-title">{adminMode === "website" ? "Website beheren" : "Intern portaal"}</h1></div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={logout} className="rounded-md">Uitloggen</Button>
          {adminMode === "website" && <Button onClick={save} disabled={saving} className="rounded-md bg-accent hover:bg-accent/90">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Opslaan</Button>}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button type="button" onClick={() => setAdminMode("website")} variant={adminMode === "website" ? "default" : "outline"} className={`rounded-md ${adminMode === "website" ? "bg-primary hover:bg-primary/90" : ""}`}>Websitebeheer</Button>
        <Button type="button" onClick={() => setAdminMode("internal")} variant={adminMode === "internal" ? "default" : "outline"} className={`rounded-md ${adminMode === "internal" ? "bg-primary hover:bg-primary/90" : ""}`}>Interne samenwerking</Button>
      </div>

      {adminMode === "website" && <div className="mt-8 grid gap-8">
        <section className="bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-semibold">Teksten en afbeeldingen</h2>
          <p className="mt-2 text-sm text-muted-foreground">Gebruik voor afbeeldingen een volledige HTTPS-afbeeldings-URL. Laat een URL leeg om de vaste projectafbeelding te gebruiken.</p>
          <div className="mt-6 grid gap-6">
            {CONTENT_SECTIONS.map(section => (
              <div key={section.title} className="border border-border p-4">
                <h3 className="text-lg font-semibold">{section.title}</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  {section.fields.map(([key, label, type, options]) => (
                    <label key={key} className={`text-sm font-semibold ${type === "textarea" ? "sm:col-span-2" : ""}`}>
                      {label}
                      {type === "select" ? (
                        <Select value={data.content[key] ?? "normal"} onValueChange={value => updateContent(key, value)}>
                          <SelectTrigger className="mt-2 h-11 rounded-md">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {options.map(([value, optionLabel]) => (
                              <SelectItem key={value} value={value}>{optionLabel}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : type === "textarea" ? (
                        <Textarea rows={3} value={data.content[key] ?? ""} onChange={e => updateContent(key, e.target.value)} className="mt-2 rounded-md" />
                      ) : (
                        <Input value={data.content[key] ?? ""} onChange={e => updateContent(key, e.target.value)} className="mt-2 rounded-md" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-semibold">Crowdfunding</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-semibold sm:col-span-2">Titel<Input value={data.campaign.title} onChange={e => updateCampaign("title", e.target.value)} className="mt-2 rounded-md" /></label>
            <label className="text-sm font-semibold sm:col-span-2">Omschrijving<Textarea rows={3} value={data.campaign.description} onChange={e => updateCampaign("description", e.target.value)} className="mt-2 rounded-md" /></label>
            <label className="text-sm font-semibold">Doelbedrag<Input type="number" value={data.campaign.target_amount} onChange={e => updateCampaign("target_amount", Number(e.target.value))} className="mt-2 rounded-md" /></label>
            <label className="text-sm font-semibold">Opgehaald<Input type="number" value={data.campaign.current_amount} onChange={e => updateCampaign("current_amount", Number(e.target.value))} className="mt-2 rounded-md" /></label>
            <label className="flex items-center gap-3 text-sm font-semibold sm:col-span-2"><input type="checkbox" checked={data.campaign.published} onChange={e => updateCampaign("published", e.target.checked)} /> Toon crowdfunding op de website</label>
          </div>
        </section>

        <section className="bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-2xl font-semibold">Agenda</h2>
            <Button type="button" onClick={addEvent} variant="outline" className="rounded-md"><Plus className="mr-2 h-4 w-4" />Item</Button>
          </div>
          <div className="mt-5 grid gap-5">
            {data.events.length === 0 && <p className="text-muted-foreground">Nog geen agenda-items.</p>}
            {data.events.map((event, index) => <div key={event.id || index} className="grid gap-4 border p-4 sm:grid-cols-2">
              <label className="text-sm font-semibold sm:col-span-2">Titel<Input value={event.title} onChange={e => updateEvent(index, "title", e.target.value)} className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold">Datum<Input type="date" value={event.date} onChange={e => updateEvent(index, "date", e.target.value)} className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold">Tijd<Input value={event.time || ""} onChange={e => updateEvent(index, "time", e.target.value)} placeholder="Bijv. 19:30" className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold sm:col-span-2">Locatie<Input value={event.location || ""} onChange={e => updateEvent(index, "location", e.target.value)} className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold sm:col-span-2">Omschrijving<Textarea rows={3} value={event.description} onChange={e => updateEvent(index, "description", e.target.value)} className="mt-2 rounded-md" /></label>
              <div className="flex items-center justify-between gap-4 sm:col-span-2">
                <label className="flex items-center gap-3 text-sm font-semibold"><input type="checkbox" checked={event.published} onChange={e => updateEvent(index, "published", e.target.checked)} /> Gepubliceerd</label>
                <Button type="button" onClick={() => removeEvent(index)} variant="outline" className="rounded-md text-destructive"><Trash2 className="mr-2 h-4 w-4" />Verwijder</Button>
              </div>
            </div>)}
          </div>
        </section>
        <AdminKnowledgeModule api={API} />
      </div>}

      {adminMode === "internal" && <div className="mt-8 grid gap-8">
        <section className="border border-accent/20 bg-accent/10 p-5">
          <div className="flex gap-3">
            <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-accent" />
            <div>
              <h2 className="text-xl font-semibold">Intern samenwerken zonder cliëntgegevens</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Gebruik dit portaal voor bestuur, vrijwilligers, werkgroepen, vergaderingen en actiepunten. Sla hier geen medische gegevens, diagnoses, BSN-nummers of andere bijzondere persoonsgegevens op.
              </p>
            </div>
          </div>
        </section>

        <section className="grid overflow-hidden rounded-md border border-border bg-white shadow-sm lg:grid-cols-[18rem_1fr]">
          <aside className="border-b border-border bg-secondary/35 p-4 lg:border-b-0 lg:border-r">
            <p className="section-label">Omgevingen</p>
            <div className="mt-4 grid gap-2">
              {internalSpaces.map(space => {
                const active = selectedSpaceId === space.id;
                const count = internalItems.filter(item => (item.space_id || "space-general") === space.id).length;
                return <button key={space.id} type="button" onClick={() => changeInternalSpace(space.id)} className={`rounded-md px-3 py-3 text-left transition-colors ${active ? "bg-primary text-white" : "hover:bg-white"}`}>
                  <span className={`block text-[0.68rem] font-bold uppercase tracking-wide ${active ? "text-white/70" : "text-accent"}`}>{INTERNAL_SPACE_TYPE_LABELS[space.type] || space.type}</span>
                  <span className="mt-1 block font-sans text-sm font-bold leading-snug">{space.name}</span>
                  <span className={`mt-1 block text-xs ${active ? "text-white/70" : "text-muted-foreground"}`}>{count} items</span>
                </button>;
              })}
            </div>
          </aside>
          <div className="p-4 md:p-6">
            <div className="grid gap-5 xl:grid-cols-[1fr_20rem]">
              <div>
                <p className="section-label">{INTERNAL_VISIBILITY_LABELS[selectedSpace.visibility] || selectedSpace.visibility}</p>
                <h2 className="mt-2 text-3xl font-bold leading-tight">{selectedSpace.name}</h2>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">{selectedSpace.description}</p>
                {selectedSpace.visibility === "restricted" && <p className="mt-3 rounded-md bg-accent/10 p-3 text-sm text-primary">Deze bestuursomgeving is apart gezet. Echte per-persoon afscherming volgt met gebruikersaccounts en rollen.</p>}
              </div>
              <div className="grid gap-3 rounded-md border border-border bg-secondary/25 p-4">
                <label className="text-sm font-semibold">Leden
                  <Input value={peopleToInput(selectedSpace.members)} onChange={e => setInternalSpaces(prev => prev.map(space => space.id === selectedSpaceId ? { ...space, members: inputToList(e.target.value) } : space))} placeholder="Bijv. Judith, Ronny" className="mt-2 rounded-md bg-white" />
                </label>
                <label className="text-sm font-semibold">Uitnodigingen
                  <Input value={peopleToInput(selectedSpace.invitees)} onChange={e => setInternalSpaces(prev => prev.map(space => space.id === selectedSpaceId ? { ...space, invitees: inputToList(e.target.value) } : space))} placeholder="Namen of e-mailadressen" className="mt-2 rounded-md bg-white" />
                </label>
                <Button type="button" onClick={() => updateInternalSpacePeople(selectedSpaceId, "members", peopleToInput(selectedSpace.members))} disabled={savingSpace} className="rounded-md bg-primary hover:bg-primary/90">{savingSpace ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Omgeving opslaan</Button>
              </div>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-4">
              <button type="button" onClick={() => changeInternalType("meeting")} className="rounded-md bg-secondary/50 p-4 text-left hover:bg-secondary"><span className="text-xs font-bold uppercase tracking-wide text-accent">Agenda</span><span className="mt-1 block text-2xl font-bold text-primary">{scopedMeetings.length}</span></button>
              <button type="button" onClick={() => changeInternalType("action")} className="rounded-md bg-secondary/50 p-4 text-left hover:bg-secondary"><span className="text-xs font-bold uppercase tracking-wide text-accent">Actiepunten</span><span className="mt-1 block text-2xl font-bold text-primary">{scopedOpenActions.length}</span></button>
              <button type="button" onClick={() => changeInternalType("document")} className="rounded-md bg-secondary/50 p-4 text-left hover:bg-secondary"><span className="text-xs font-bold uppercase tracking-wide text-accent">Documenten</span><span className="mt-1 block text-2xl font-bold text-primary">{scopedDocuments.length}</span></button>
              <button type="button" onClick={() => changeInternalType("decision")} className="rounded-md bg-secondary/50 p-4 text-left hover:bg-secondary"><span className="text-xs font-bold uppercase tracking-wide text-accent">Besluiten</span><span className="mt-1 block text-2xl font-bold text-primary">{scopedDecisions.length}</span></button>
            </div>
            <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_20rem]">
              <InternalCalendar monthDate={calendarMonth} meetings={scopedMeetings} onEdit={editInternalItem} onMonthChange={changeCalendarMonth} />
              <div className="grid content-start gap-4">
                <div className="rounded-md border border-border p-4">
                  <h3 className="font-sans text-base font-bold">Komende afspraken</h3>
                  <div className="mt-3 grid gap-2">{scopedUpcomingMeetings.length ? scopedUpcomingMeetings.map(item => <button key={item.id} type="button" onClick={() => editInternalItem(item)} className="rounded-md bg-secondary/45 p-3 text-left text-sm hover:bg-secondary"><strong>{item.date || "Geen datum"}</strong><br />{item.title}</button>) : <p className="text-sm text-muted-foreground">Geen geplande afspraken.</p>}</div>
                </div>
                <div className="rounded-md border border-border p-4">
                  <h3 className="font-sans text-base font-bold">Documenten en notulen</h3>
                  <div className="mt-3 grid gap-2">{scopedDocuments.length ? scopedDocuments.map(item => <button key={item.id} type="button" onClick={() => editInternalItem(item)} className="rounded-md bg-secondary/45 p-3 text-left text-sm hover:bg-secondary">{item.title}</button>) : <p className="text-sm text-muted-foreground">Nog geen documenten in deze omgeving.</p>}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="hidden bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="section-label">Omgevingen</p>
              <h2 className="text-2xl font-semibold">Kies waar u werkt</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                De algemene omgeving is voor iedereen intern. Werkgroepen hebben hun eigen agenda, documenten en acties. Het algemeen bestuur is voorbereid als afgeschermde omgeving; echte blokkering per persoon volgt zodra gebruikersaccounts en rollen actief zijn.
              </p>
            </div>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {internalSpaces.map(space => {
              const active = selectedSpaceId === space.id;
              const count = internalItems.filter(item => (item.space_id || "space-general") === space.id).length;
              return <button key={space.id} type="button" onClick={() => changeInternalSpace(space.id)} className={`rounded-md border p-4 text-left transition-colors ${active ? "border-primary bg-primary text-white" : "border-border bg-white hover:border-primary/50"}`}>
                <span className={`text-xs font-bold uppercase tracking-wide ${active ? "text-white/75" : "text-accent"}`}>{INTERNAL_SPACE_TYPE_LABELS[space.type] || space.type}</span>
                <span className="mt-2 block text-lg font-semibold">{space.name}</span>
                <span className={`mt-2 block text-sm ${active ? "text-white/75" : "text-muted-foreground"}`}>{INTERNAL_VISIBILITY_LABELS[space.visibility] || space.visibility} · {count} items</span>
              </button>;
            })}
          </div>
          <div className="mt-5 grid gap-4 border-t border-border pt-5 lg:grid-cols-[1fr_1fr]">
            <div>
              <h3 className="font-semibold">{selectedSpace.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{selectedSpace.description}</p>
              {selectedSpace.visibility === "restricted" && <p className="mt-3 rounded-md bg-accent/10 p-3 text-sm text-primary">Let op: deze bestuursomgeving is inhoudelijk apart gezet. Echte toegangscontrole vraagt nog gebruikersaccounts en server-side rollen.</p>}
            </div>
            <div className="grid gap-3">
              <label className="text-sm font-semibold">Leden
                <Input value={peopleToInput(selectedSpace.members)} onChange={e => setInternalSpaces(prev => prev.map(space => space.id === selectedSpaceId ? { ...space, members: inputToList(e.target.value) } : space))} placeholder="Bijv. Judith, Ronny" className="mt-2 rounded-md" />
              </label>
              <label className="text-sm font-semibold">Uitnodigingen / toe te voegen personen
                <Input value={peopleToInput(selectedSpace.invitees)} onChange={e => setInternalSpaces(prev => prev.map(space => space.id === selectedSpaceId ? { ...space, invitees: inputToList(e.target.value) } : space))} placeholder="Namen of e-mailadressen, komma-gescheiden" className="mt-2 rounded-md" />
              </label>
              <Button type="button" onClick={() => updateInternalSpacePeople(selectedSpaceId, "members", peopleToInput(selectedSpace.members))} disabled={savingSpace} className="w-fit rounded-md bg-primary hover:bg-primary/90">{savingSpace ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Omgeving opslaan</Button>
            </div>
          </div>
        </section>

        <section className="hidden gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {INTERNAL_TYPES.slice(0, 8).map(([type, label]) => (
            <button key={type} type="button" onClick={() => changeInternalType(type)} className={`rounded-md border p-4 text-left transition-colors ${internalType === type ? "border-primary bg-primary text-white" : "border-border bg-white hover:border-primary/50"}`}>
              <span className="text-sm font-semibold">{label}</span>
              <span className={`mt-2 block text-2xl font-bold ${internalType === type ? "text-white" : "text-primary"}`}>{scopedInternalItems.filter(item => item.type === type).length}</span>
            </button>
          ))}
        </section>

        <section className="grid gap-8 lg:grid-cols-[.95fr_1.05fr]">
          <div className="bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-semibold">{editingInternalId ? "Intern item bewerken" : "Nieuw intern item"}</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-semibold">Onderdeel
                <Select value={internalType} onValueChange={changeInternalType}>
                  <SelectTrigger className="mt-2 h-11 rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>{INTERNAL_TYPES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                </Select>
              </label>
              <label className="text-sm font-semibold">Status
                <Select value={internalForm.status} onValueChange={value => updateInternalForm("status", value)}>
                  <SelectTrigger className="mt-2 h-11 rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>{(INTERNAL_STATUS_OPTIONS[internalType] || []).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                </Select>
              </label>
              <label className="text-sm font-semibold sm:col-span-2">Omgeving
                <Select value={internalForm.space_id || selectedSpaceId} onValueChange={value => { updateInternalForm("space_id", value); setSelectedSpaceId(value); }}>
                  <SelectTrigger className="mt-2 h-11 rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>{internalSpaces.map(space => <SelectItem key={space.id} value={space.id}>{space.name}</SelectItem>)}</SelectContent>
                </Select>
              </label>
              <label className="text-sm font-semibold sm:col-span-2">Titel<Input value={internalForm.title} onChange={e => updateInternalForm("title", e.target.value)} className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold sm:col-span-2">Omschrijving<Textarea rows={5} value={internalForm.description} onChange={e => updateInternalForm("description", e.target.value)} className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold">Eigenaar of verantwoordelijke<Input value={internalForm.owner} onChange={e => updateInternalForm("owner", e.target.value)} className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold">Werkgroep<Input value={internalForm.workgroup} onChange={e => updateInternalForm("workgroup", e.target.value)} placeholder="Bijv. Kwaliteit en zorginhoud" className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold">Datum<Input type="date" value={internalForm.date} onChange={e => updateInternalForm("date", e.target.value)} className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold">Locatie<Input value={internalForm.location} onChange={e => updateInternalForm("location", e.target.value)} placeholder="Bijv. Eelinkstraat 57 of online" className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold">Deadline<Input type="date" value={internalForm.deadline} onChange={e => updateInternalForm("deadline", e.target.value)} className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold sm:col-span-2">Deelnemers of genodigden<Input value={peopleToInput(internalForm.participants)} onChange={e => updateInternalForm("participants", e.target.value)} placeholder="Bijv. Refaim Pietersz, André, Ronny" className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold sm:col-span-2">Document-, notulen- of vergaderlink<Input value={internalForm.url} onChange={e => updateInternalForm("url", e.target.value)} placeholder="https://... (veilige upload volgt via opslagkoppeling)" className="mt-2 rounded-md" /></label>
              <label className="text-sm font-semibold sm:col-span-2">Tags<Input value={Array.isArray(internalForm.tags) ? internalForm.tags.join(", ") : internalForm.tags} onChange={e => updateInternalForm("tags", e.target.value)} placeholder="bijv. bestuur, financiën, vrijwilligers" className="mt-2 rounded-md" /></label>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button type="button" onClick={saveInternalItem} disabled={savingInternal || !internalForm.title.trim()} className="rounded-md bg-accent hover:bg-accent/90">{savingInternal ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{editingInternalId ? "Bijwerken" : "Opslaan"}</Button>
              {editingInternalId && <Button type="button" variant="outline" onClick={() => { setEditingInternalId(""); setInternalForm({ ...createInternalForm(internalType), space_id: selectedSpaceId }); }} className="rounded-md">Nieuw item</Button>}
            </div>
          </div>

          <div className="grid gap-5">
            <div className="bg-white p-6 shadow-sm">
              <h2 className="text-2xl font-semibold">Dashboard</h2>
              <div className="mt-5 grid gap-4">
                <div><h3 className="font-semibold">Open actiepunten in {selectedSpace.name}</h3>{scopedOpenActions.length ? scopedOpenActions.map(item => <button key={item.id} type="button" onClick={() => editInternalItem(item)} className="mt-2 block w-full border-l-2 border-accent bg-secondary/40 p-3 text-left text-sm">{item.title}</button>) : <p className="mt-2 text-sm text-muted-foreground">Geen open actiepunten.</p>}</div>
                <div><h3 className="font-semibold">Agenda in {selectedSpace.name}</h3>{scopedUpcomingMeetings.length ? scopedUpcomingMeetings.map(item => <button key={item.id} type="button" onClick={() => editInternalItem(item)} className="mt-2 block w-full border-l-2 border-primary bg-secondary/40 p-3 text-left text-sm">{item.date || "Geen datum"} - {item.title}</button>) : <p className="mt-2 text-sm text-muted-foreground">Geen geplande vergaderingen.</p>}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold">{INTERNAL_TYPE_LABELS[internalType]}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{selectedSpace.name}</p>
            </div>
            <p className="text-sm text-muted-foreground">{scopedTypeItems.length} items</p>
          </div>
          <div className="mt-5 grid gap-4">
            {scopedTypeItems.length === 0 && <p className="text-muted-foreground">Nog niets opgeslagen voor dit onderdeel in deze omgeving.</p>}
            {scopedTypeItems.map(item => (
              <article key={item.id} className="border border-border p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-accent">{item.status}</p>
                    <h3 className="mt-1 text-lg font-semibold">{item.title}</h3>
                    {item.description && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.description}</p>}
                    <p className="mt-3 text-xs text-muted-foreground">{item.owner && `Eigenaar: ${item.owner} `}{item.date && `Datum: ${item.date} `}{item.location && `Locatie: ${item.location} `}{item.deadline && `Deadline: ${item.deadline} `}{item.workgroup && `Werkgroep: ${item.workgroup}`}</p>
                    {item.participants?.length > 0 && <p className="mt-2 text-xs text-muted-foreground">Deelnemers: {item.participants.join(", ")}</p>}
                    {item.url && <a href={item.url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm font-semibold text-primary underline">Open link</a>}
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => editInternalItem(item)} className="rounded-md">Bewerk</Button>
                    <Button type="button" variant="outline" onClick={() => deleteInternalItem(item.id)} className="rounded-md text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>}
    </div>
  </section></PageShell>;
};

const Footer = () => {
  const siteData = useSiteData();
  const [email, setEmail] = useState("");
  const subscribe = async e => { e.preventDefault(); try { const {data} = await axios.post(`${API}/newsletter/subscribe`, {email}); toast.success(data.message); setEmail(""); } catch { toast.error("Aanmelden lukt nu niet."); } };
  return <footer className="bg-primary py-10 text-white md:py-14"><div className="editorial-container"><div className="grid gap-8 md:grid-cols-[1.1fr_.9fr_.9fr]">
    <div><Logo /><p className="mt-5 max-w-sm text-sm leading-relaxed text-white/70">{siteData.content.footer_text}</p></div>
    <div className="border-t border-white/10 pt-6 md:border-t-0 md:pt-0"><h3 className="font-sans text-base font-bold">Volg de ontwikkeling</h3><form onSubmit={subscribe} className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] md:grid-cols-1 lg:grid-cols-[1fr_auto]"><Input type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="E-mailadres" className="h-11 rounded-full border-white/20 bg-white/10 text-white placeholder:text-white/50" /><Button className="h-11 rounded-full bg-accent px-6 hover:bg-accent/90">Aanmelden</Button></form></div>
    <div className="border-t border-white/10 pt-6 md:border-t-0 md:pt-0"><h3 className="font-sans text-base font-bold">Stichtingsgegevens</h3><address className="mt-4 text-sm not-italic leading-relaxed text-white/70"><strong className="font-semibold text-white">{ORGANIZATION.name}</strong><br />{ORGANIZATION.streetAddress}<br />{ORGANIZATION.postalAddress}</address><p className="mt-3 text-sm text-white/70">KvK-nummer: {ORGANIZATION.chamberOfCommerce}</p><div className="mt-5 flex gap-4"><a href="https://facebook.com" aria-label="Facebook"><Facebook className="h-5 w-5" /></a><a href="https://instagram.com" aria-label="Instagram"><Instagram className="h-5 w-5" /></a></div></div>
  </div><div className="mt-8 border-t border-white/10 pt-6 text-xs text-white/50">© {new Date().getFullYear()} Rumah Kasih</div></div></footer>;
};

const DonationSuccess = () => {
  const [params] = useSearchParams(); const navigate = useNavigate(); const [state, setState] = useState("loading"); const [amount, setAmount] = useState(null);
  useEffect(() => {
    const session = params.get("session_id"); if (!session) { setState("error"); return; }
    let attempts = 0; const check = async () => { try { const {data} = await axios.get(`${API}/donation/status/${session}`); setAmount(data.amount); if (data.payment_status === "paid") setState("success"); else if (++attempts < 5) setTimeout(check, 2000); else setState("error"); } catch { setState("error"); } }; check();
  }, [params]);
  return <PageShell><section className="grid min-h-[65vh] place-items-center px-4 py-16 text-center"><div>{state === "loading" ? <><Loader2 className="mx-auto h-12 w-12 animate-spin text-accent" /><h1 className="mt-5 text-3xl font-bold">We controleren uw betaling</h1></> : state === "success" ? <><CheckCircle2 className="mx-auto h-14 w-14 text-accent" /><h1 className="mt-5 text-3xl font-bold">Hartelijk dank</h1><p className="mt-3 text-muted-foreground">Uw bijdrage van EUR {Number(amount).toFixed(2)} is ontvangen.</p></> : <><X className="mx-auto h-14 w-14 text-destructive" /><h1 className="mt-5 text-3xl font-bold">De betaling kon niet worden bevestigd</h1><p className="mt-3 text-muted-foreground">Neem contact met ons op als het bedrag wel is afgeschreven.</p></>}<Button onClick={() => navigate("/")} className="mt-7 rounded-md">Terug naar de website</Button></div></section></PageShell>;
};

export default function App() {
  return <BrowserRouter><SiteDataProvider><ScrollToTop /><Toaster position="top-center" richColors /><Routes>
    <Route path="/" element={<Home />} />
    <Route path="/dagbesteding" element={<DayCarePage />} />
    <Route path="/mantelzorgers" element={<CaregiversPage />} />
    <Route path="/kennis-en-nieuws" element={<PageShell><KnowledgeNewsPage api={API} /></PageShell>} />
    <Route path="/kennis-en-nieuws/:slug" element={<PageShell><KnowledgeArticlePage api={API} /></PageShell>} />
    <Route path="/agenda" element={<AgendaPage />} />
    <Route path="/steun-ons" element={<SupportPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="/admin" element={<AdminPage />} />
    <Route path="/donatie-succes" element={<DonationSuccess />} />
  </Routes></SiteDataProvider></BrowserRouter>;
}
